import React from 'react';
import { SupplementConfig } from '../../types/config';
import { ScoredSupplement } from '../../utils/supplementRecommendation';
import { formatPrice } from '../../utils/formatters';
import { useAppConfig } from '../../context/AppConfigContext';
import { useModalScrollLock } from './useModalScrollLock';

interface SupplementDetailModalProps {
  scored: ScoredSupplement;
  onClose: () => void;
  onAdd?: () => void;
  isSelected?: boolean;
}

function displayName(supplement: SupplementConfig): string {
  return (
    (supplement.name || '')
      .replace(/^Suplemento\s+/i, '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim() || supplement.name
  );
}

export default function SupplementDetailModal({ scored, onClose, onAdd, isSelected }: SupplementDetailModalProps) {
  const { t } = useAppConfig();
  useModalScrollLock();
  const supplement = scored.supplement;
  const title = displayName(supplement);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-hidden"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-xl text-gray-500 shadow-sm"
          aria-label={t('supplementDetail.close_aria', 'Cerrar')}
        >
          ×
        </button>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {/* Header con imagen */}
          <div className="bg-[#f5f5f5] px-6 pt-3 pb-1 sm:pt-4 sm:pb-2">
            {supplement.image_url ? (
              <img src={supplement.image_url} alt={title} className="w-40 h-40 object-contain mx-auto" />
            ) : (
              <div className="w-40 h-40 flex items-center justify-center text-6xl mx-auto">{supplement.emoji}</div>
            )}
          </div>

          <div className="px-4 pt-2 pb-4 sm:px-6 sm:pt-3 sm:pb-6 space-y-2 sm:space-y-3">
            <h2 className="font-fredoka text-xl font-bold text-[#1a1a1a] text-center">{title}</h2>
            <p className="font-poppins text-sm text-[#666] text-center">{supplement.description}</p>

            {scored.caution && (
              <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-4">
                <h3 className="text-xs font-bold text-[#B45309] uppercase tracking-wider mb-2">
                  {t('supplementDetail.caution', 'Antes de darlo, ten en cuenta')}
                </h3>
                <p className="font-poppins text-sm text-[#78350F] flex items-start gap-2">
                  <span className="mt-0.5">⚠️</span>
                  <span>{scored.caution.message}</span>
                </p>
              </div>
            )}

            {scored.recommendationReasons.length > 0 && (
              <div className="bg-[#f0f9fc] rounded-xl p-4">
                <h3 className="text-xs font-bold text-[#008BBC] uppercase tracking-wider mb-2">
                  {t('supplementDetail.why', '¿Por qué lo recomendamos?')}
                </h3>
                <ul className="space-y-1.5">
                  {scored.recommendationReasons.map((r, i) => (
                    <li key={i} className="font-poppins text-sm text-[#1a1a1a] flex items-start gap-2">
                      <span className="text-[#008BBC] mt-0.5">✦</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {supplement.benefits && supplement.benefits.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-[#008BBC] uppercase tracking-wider mb-2">
                  {t('supplementDetail.benefits', 'Beneficios')}
                </h3>
                <ul className="space-y-1.5">
                  {supplement.benefits.map((b, i) => (
                    <li key={i} className="font-poppins text-sm text-[#666] flex items-start gap-2">
                      <span className="text-[#4caf50] mt-0.5">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white px-4 py-2.5 sm:px-6">
          <span className="font-fredoka text-xl font-black text-[#1a1a1a]">{formatPrice(supplement.price_eur)}</span>
          {onAdd && (
            <button
              onClick={onAdd}
              className={`py-2 px-5 rounded-xl font-fredoka text-sm font-bold transition-all ${
                isSelected ? 'bg-[#e8f5e9] text-[#2e7d32]' : 'bg-[#008BBC] text-white hover:bg-[#007aa8]'
              }`}
            >
              {isSelected ? t('supplementDetail.added', '✓ AÑADIDO') : t('supplementDetail.add', 'AÑADIR')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { displayName as supplementDisplayName };
